import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { assetSubResourceBreadcrumb } from '../../../../../../utils/breadcrumb.utils';
import { MenuItem } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { HasPermissionDirective } from '../../../../../../directives/has-permission.directive';
import {
  AuscultationDataTypes,
  AuscultationInformationDefinition,
} from '../../../../../../entity-definitions/auscultation-information.definition';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { EntityDialogService } from '../../../../../../services/entity-dialog.service';
import { ToastService } from '../../../../../../services/toast.service';
import { AuscultationInformation, AuscultationInformationStore } from '../../../../../../stores/auscultation-info.store';
import { ChoiceListStore } from '../../../../../../stores/choice-list.store';
import { LocationStore } from '../../../../../../stores/location.store';
import { isEntityFormDeleteAction } from '../../../../../../types/entity-form-result.interface';
import { MenuItemWithIcon } from '../../../../../../types/menu.interface';
import { RouteUtils } from '../../../../../../utils/route.utils';
import { CesAssetBreadcrumbComponent } from '../../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonSecondaryComponent, CesMenuButtonSecondaryComponent } from '../../../../../ui/ces-buttons';
import { CesProperty } from '../../../../../ui/ces-property/ces-property.component';

@Component({
  selector: 'app-auscultation-info-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    TagModule,
    CesAssetBreadcrumbComponent,
    CesButtonSecondaryComponent,
    CesMenuButtonSecondaryComponent,
    HasPermissionDirective,
    CesProperty,
  ],
  templateUrl: './auscultation-info-detail.component.html',
})
export class AuscultationInfoDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslocoService);
  private readonly toast = inject(ToastService);
  private readonly store = inject(AuscultationInformationStore);
  private readonly locationStore = inject(LocationStore);
  private readonly choiceListStore = inject(ChoiceListStore);
  private readonly dialogService = inject(EntityDialogService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);

  protected readonly assetId = signal<number | null>(null);
  protected readonly entity = signal<AuscultationInformation | undefined>(undefined);
  protected readonly ready = signal(false);
  private readonly translations = toSignal(this.translate.selectTranslation());

  protected readonly dataTypeLabel = computed(() => {
    this.translations();
    const current = this.entity();
    if (!current) return '';
    const match = AuscultationDataTypes.find(dt => dt.value === current.type);
    return match ? this.translate.translate(match.labelKey) : String(current.type);
  });

  protected readonly choiceListName = computed(() => {
    const current = this.entity();
    if (!current?.choiceListId) return '';
    return this.choiceListStore.byId()(current.choiceListId)?.name ?? '';
  });

  protected readonly locationNames = computed<string[]>(() => {
    const current = this.entity();
    if (!current?.locationIds?.length) return [];
    return current.locationIds.map(id => this.locationStore.byId()(id)?.name).filter((name): name is string => Boolean(name));
  });

  protected readonly isUsed = computed(() => this.entity()?.isUsed ?? false);

  protected readonly deleteMenuItems = computed<MenuItemWithIcon[]>(() => {
    this.translations();
    if (!this.entity()) return [];
    const used = this.isUsed();
    return [
      {
        label: this.translate.translate('common.buttons.delete'),
        materialIcon: 'delete',
        styleClass: 'p-menuitem-danger',
        disabled: used,
        ...(used
          ? { title: this.translate.translate('entities.auscultationInformation.messages.deleteError'), tooltipPosition: 'left' }
          : { command: () => this.confirmDelete() }),
      },
    ];
  });

  protected readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return assetSubResourceBreadcrumb(this.translate, {
      parentLabelKey: 'navigation.measures',
      parentRouterLink: ['/assets', this.assetId(), 'configuration', 'measures', 'auscultation-information'],
      currentLabel: this.entity()?.name,
    });
  });

  ngOnInit(): void {
    this.assetId.set(RouteUtils.getAssetIdFromRoute(this.route));

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const rawId = params.get('auscId');
      const parsed = Number(rawId);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        void this.router.navigate(['/404']);
        return;
      }
      this.ready.set(false);
      void this.loadData(parsed);
    });
  }

  private async loadData(id: number): Promise<void> {
    const assetId = this.assetId();
    try {
      const tasks: Promise<unknown>[] = [this.store.loadById(id), this.choiceListStore.loadAll()];
      if (assetId) tasks.push(this.locationStore.getLocationsByAsset(assetId));
      const [loaded] = await Promise.all(tasks);
      this.entity.set(loaded as AuscultationInformation | undefined);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error(this.translate.translate('common.errors.auscultationInfoLoadFailed'), msg);
      void this.router.navigate(['/404']);
    } finally {
      this.ready.set(true);
    }
  }

  protected editEntity(): void {
    const assetId = this.assetId();
    const current = this.entity();
    if (!assetId || !current?.id) return;

    this.dialogService
      .openEditDialog<AuscultationInformation>(current, {
        header: AuscultationInformationDefinition.messages.dialogTitles.edit(),
        headerParams: { name: current.name },
        entityDefinition: AuscultationInformationDefinition,
        mode: 'edit',
        parentId: assetId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result || isEntityFormDeleteAction(result)) return;
        void this.applyUpdate(current.id, result);
      });
  }

  private async applyUpdate(id: number, payload: Partial<AuscultationInformation>): Promise<void> {
    try {
      await this.store.update(id, payload);
      const refreshed = await this.store.loadById(id);
      this.entity.set(refreshed);
    } catch (error) {
      this.toast.warning('common.errors.saveFailed');
      const msg = error instanceof Error ? error.message : String(error);
      console.error(this.translate.translate('common.errors.saveFailed'), msg);
    }
  }

  private confirmDelete(): void {
    const current = this.entity();
    if (!current) return;
    this.confirmationHelper.confirmDelete(current.name, 'entities.auscultationInformation.messages.deleteConfirm', () =>
      this.onDelete(),
    );
  }

  protected async onDelete(): Promise<void> {
    const current = this.entity();
    if (!current?.id) return;
    try {
      await this.store.delete(current.id);
      void this.navigateBack();
    } catch (error) {
      this.toast.warning('entities.auscultationInformation.messages.deleteError');
      const msg = error instanceof Error ? error.message : String(error);
      console.error(this.translate.translate('common.errors.auscultationInfoDeleteFailed'), msg);
    }
  }

  private async navigateBack(): Promise<void> {
    const id = this.assetId();
    if (!id) {
      await this.router.navigate(['/']);
      return;
    }
    await this.router.navigate(['/assets', id, 'configuration', 'measures', 'auscultation-information']);
  }
}
